// import firebase from "firebase/app";
import { initializeApp } from "firebase/app";
import {
  User,
  getAuth,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
} from "firebase/auth";
import {
  Firestore,
  getFirestore,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  updateDoc,
} from "firebase/firestore";
import { firebaseConfig } from "./env";

import "firebase/auth";
import "firebase/firestore";

export type UserEntity = {
  admin: boolean;
  name: string;
  department: string;
  superAdmin: boolean;
  superBoards: string[];
};

const isUserEntity = (args: any): args is UserEntity => {
  try {
    return (
      typeof args === "object" &&
      typeof args.admin === "boolean" &&
      typeof args.name === "string" &&
      typeof args.department === "string" &&
      typeof args.superAdmin === "boolean" &&
      Array.isArray(args.superBoards) &&
      args.superBoards.every((item: any) => typeof item === "string")
    );
  } catch (_err) {
    return false;
  }
};

export type MenuEntity = {
  service: string;
  active: boolean;
  alt?: string;
};

const isMenuEntity = (args: any): args is MenuEntity => {
  try {
    return (
      typeof args === "object" &&
      typeof args.service === "string" &&
      typeof args.active === "boolean" &&
      (typeof args.alt === "undefined" || typeof args.alt === "string")
    );
  } catch (_err) {
    return false;
  }
};

export type BoardEntity = {
  customerId: number;
  shared: boolean;
  name: string;
  services: {
    security?: string;
    netFinder?: string;
    vulnTracker?: string;
    sysMonitor?: string;
    l2Blocker?: string;
    dataAnalyzer?: string;
    dataProtect?: string;
    teleManager?: string;
  };
  members: string[];
  menus: MenuEntity[];
  permissions: {
    uid: string;
    [key: string]: string;
  }[];
  scanConfig?: {
    includes: string[];
  };
};

const isBoardEntity = (args: any): args is BoardEntity => {
  try {
    return (
      typeof args === "object" &&
      typeof args.customerId === "number" &&
      typeof args.shared === "boolean" &&
      typeof args.name === "string" &&
      typeof args.services === "object" &&
      Array.isArray(args.members) &&
      args.members.every((item: any) => typeof item === "string") &&
      Array.isArray(args.menus) &&
      args.menus.every((item: any) => isMenuEntity(item)) &&
      Array.isArray(args.permissions) &&
      args.permissions.every((item: any) => {
        return typeof item === "object" && typeof item.uid === "string";
      }) &&
      (typeof args.scanConfig === "undefined" ||
        (Array.isArray(args.scanConfig.includes) &&
          args.scanConfig.includes.every(
            (item: any) => typeof item === "string",
          )))
    );
  } catch (_err) {
    return false;
  }
};

export type AssetViewEntity = {
  views: {
    name: string;
    shown: {
      basics: boolean[];
      additionals: boolean[];
    };
    filters: {
      target: string;
      condition: string;
      value: string;
    }[];
  }[];
};

const isAssetViewEntity = (args: any): args is AssetViewEntity => {
  try {
    return (
      typeof args === "object" &&
      Array.isArray(args.views) &&
      args.views.every((view: any) => {
        return (
          typeof view === "object" &&
          typeof view.name === "string" &&
          typeof view.shown === "object" &&
          Array.isArray(view.shown.basics) &&
          view.shown.basics.every((basic: any) => typeof basic === "boolean") &&
          view.shown.additionals.every(
            (additional: any) => typeof additional === "boolean",
          ) &&
          Array.isArray(view.filters) &&
          view.filters.every((filter: any) => {
            return (
              typeof filter === "object" &&
              typeof filter.target === "string" &&
              typeof filter.condition === "string" &&
              typeof filter.value === "string"
            );
          })
        );
      })
    );
  } catch (_err) {
    return false;
  }
};

export type SsoMaintenanceEntity = {
  startDate: string;
  startTime: string;
  endDate?: string;
  endTime: string;
  content: string;
};

const isSsoMaintenanceEntity = (args: any): args is SsoMaintenanceEntity => {
  // 明らかに日付や時刻の書式ではないものは除外
  const regexDate = /^\d{4}[-/.]\d{1,2}[-/.]\d{1,2}$/;
  const regexTime = /^\d{1,2}:[0-5][0-9]$/;
  try {
    return (
      typeof args === "object" &&
      typeof args.startDate === "string" &&
      regexDate.test(args.startDate) &&
      typeof args.startTime === "string" &&
      regexTime.test(args.startTime) &&
      (typeof args.endDate === "undefined" ||
        (typeof args.endDate === "string" && regexDate.test(args.endDate))) &&
      typeof args.endTime === "string" &&
      regexTime.test(args.endTime) &&
      typeof args.content === "string"
    );
  } catch (_err) {
    return false;
  }
};

export type SsoApplicationEntity = {
  name: string;
  url: string;
};

const isSsoApplicationEntity = (args: any): args is SsoApplicationEntity => {
  try {
    return (
      typeof args === "object" &&
      typeof args.name === "string" &&
      typeof args.url === "string"
    );
  } catch (_err) {
    return false;
  }
};

export class Firebase {
  private static _instance: Firebase;

  public static get instance(): Firebase {
    if (!this._instance) {
      this._instance = new Firebase();
    }
    return this._instance;
  }

  private firebaseUser: User | null = null;

  private isPreparedAuth: boolean = false;

  private db: Firestore | null = null;

  private constructor() {
    initializeApp(firebaseConfig());
    onAuthStateChanged(getAuth(), (user) => {
      this.firebaseUser = user;
      this.isPreparedAuth = true;
    });
    this.db = getFirestore();
  }

  public getFirebaseUser() {
    return new Promise(
      (resolve: (res: User | null) => void, reject: (err: Error) => void) => {
        if (this.isPreparedAuth) {
          resolve(this.firebaseUser);
        } else {
          const unsubscribe = onAuthStateChanged(
            getAuth(),
            (user) => {
              unsubscribe();
              resolve(user);
            },
            (err) => {
              unsubscribe();
              reject(err);
            },
          );
        }
      },
    );
  }

  public async signIn(email: string, password: string) {
    const cred = await signInWithEmailAndPassword(
      getAuth(),
      email,
      password,
    ).catch((err) => {
      throw new Error(err);
    });
    return cred;
  }

  public async signOut() {
    await signOut(getAuth()).catch((err) => {
      throw new Error(err);
    });
    return;
  }

  public async resetPassword(email: string) {
    await sendPasswordResetEmail(getAuth(), email, {
      url: window.location.origin + "/login",
    }).catch((err) => {
      throw new Error(err);
    });
    return;
  }

  public async fetchUser() {
    if (!this.db) return null;

    // eslint-disable-next-line
    const firebaseUser = await this.getFirebaseUser().catch((_err) => null);
    if (!firebaseUser) return null;

    const document = await getDoc(
      doc(collection(this.db, "users"), firebaseUser.uid),
      // eslint-disable-next-line
    ).catch((_err) => null);
    if (!document || !document.exists()) return null;

    const data = document.data();
    return isUserEntity(data) ? { id: firebaseUser.uid, data: data } : null;
  }

  public async fetchBoards() {
    if (!this.db) return null;

    // const firebaseUser = await this.getFirebaseUser().catch(_err => null);
    // if (!firebaseUser) return null;
    const user = await this.fetchUser();
    if (!user) return null;
    const uid = user.id;
    const admin = user.data.admin;

    let qs = null;
    if (admin) {
      qs = await getDocs(collection(this.db, "boards"))
        // eslint-disable-next-line
        .catch((_err) => null);
    } else {
      qs = await getDocs(
        query(
          collection(this.db, "boards"),
          where("members", "array-contains", uid),
        ),
        // eslint-disable-next-line
      ).catch((_err) => null);
    }
    if (!qs || qs.empty) return null;

    const result: { id: string; data: BoardEntity }[] = [];
    qs.forEach((doc) => {
      const data = doc.data();
      const id = doc.id;
      if (isBoardEntity(data)) {
        result.push({ id: id, data: data });
      }
    });
    return result;
  }

  public async fetchAssetViews(admin: boolean, customerId: number) {
    if (!this.db) return null;

    // eslint-disable-next-line
    const firebaseUser = await this.getFirebaseUser().catch((_err) => null);
    if (!firebaseUser) return null;

    let q = query(
      collection(this.db, "vnssCustomers"),
      where("customerId", "==", customerId),
    );
    if (!admin) {
      // セキュリティルールを満たすような検索条件を指定しないとクエリ全体が拒否される
      // 参考 https://firebase.google.com/docs/firestore/security/rules-conditions#rules_are_not_filters
      q = query(q, where("superAdmins", "array-contains", firebaseUser.uid));
    }
    // eslint-disable-next-line
    const qs = await getDocs(q).catch((_err) => null);
    if (!qs || qs.empty) return null;

    const result: { id: string; data: AssetViewEntity }[] = [];
    const id = qs.docs[0].id;
    const view = await getDoc(doc(collection(this.db, "vnssAssetViews"), id))
      // eslint-disable-next-line
      .catch((_err) => null);
    if (view && view.exists()) {
      const data = view.data();
      if (isAssetViewEntity(data)) {
        result.push({ id: id, data: data });
      }
    }
    return result;
  }

  public async syncAssetView(payload: { id: string; data: AssetViewEntity }) {
    if (!this.db) return;

    updateDoc(
      doc(collection(this.db, "vnssAssetViews"), payload.id),
      payload.data,
    );
  }

  public async fetchSsoMaintenance(limitDate: string) {
    if (!this.db) return null;

    const q = query(
      collection(this.db, "ssoMaintenance"),
      where("startDate", ">", limitDate),
    );
    // eslint-disable-next-line
    const qs = await getDocs(q).catch((_err) => null);
    if (!qs || qs.empty) return null;

    const result: { id: string; data: SsoMaintenanceEntity }[] = [];
    qs.forEach((doc) => {
      const data = doc.data();
      const id = doc.id;
      if (isSsoMaintenanceEntity(data)) {
        result.push({ id: id, data: data });
      }
    });
    return result;
  }

  public async fetchSsoApplication() {
    if (!this.db) return null;

    const qs = await getDocs(collection(this.db, "ssoApplication"))
      // eslint-disable-next-line
      .catch((_err) => null);
    if (!qs || qs.empty) return null;

    const result: { id: string; data: SsoApplicationEntity }[] = [];
    qs.forEach((doc) => {
      const data = doc.data();
      const id = doc.id;
      if (isSsoApplicationEntity(data)) {
        result.push({ id: id, data: data });
      }
    });
    return result;
  }
}

export default Firebase.instance;
